import React, { useState, useEffect, useMemo } from "react";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { BsPlusSquareDotted } from "react-icons/bs";
import { useFormik, Field, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import { Form, Label, Input, Button, Row, Col, FormText } from "reactstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import axiosInstance from "pages/Utility/axiosInstance";
import { showSuccess } from "helpers/notification_helper";
import { fileToBase64, generateVariants } from "helpers/common_helper";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsersRequest } from "store/actions";
import { useUserRole } from "hooks/useUserRole";
import Breadcrumb from "components/Common/Breadcrumb2";
import { Spinner } from "reactstrap";

const ProductForm = ({ onCreated }) => {
  const { role, vendorName, isAdmin, isVendor } = useUserRole();
  const dispatch = useDispatch();
  const allUsers = useSelector((state) => state.User.users?.data || []);
  const vendors = allUsers.filter(
    (user) => user?.role?.role_name?.toLowerCase() === "vendor"
  );

  const [allImages, setAllImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [collections, setCollections] = useState([]);
  const [marketsList, setMarketsList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [marketInventory, setMarketInventory] = useState({});
  const [inventoryByVariant, setInventoryByVariant] = useState({});
  const [productDetails, setProductDetails] = useState([]);
  const [provinces, setProvinces] = useState([]);

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const id = query.get("id");
  const navigate = useNavigate();

  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      title: "",
      body_html: "",
      vendor: "",
      product_type: "",
      tags: "",
      price: "",
      markets: [],
      options: [{ name: "Color", values: [""] }],

      images: [],
      collectionIds: [],
      provinces: [],
      status: "active",
      metafields: { vendor_name: "", vendor_id: "" },
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is Required"),
      price: Yup.number()
        .required("Product price is required")
        .typeError("Must be a number"),
      markets: Yup.array().min(1, "Select at least one market"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Convert new images to base64
        const newImagesBase64 = await Promise.all(
          allImages
            .filter((img) => img.isNew)
            .map((img) => fileToBase64(img.file))
        );

        const images = newImagesBase64.map((base64) => ({
          attachment: base64,
        }));

        // Clean options & variants
        const cleanedOptions = values.options
          .map((opt) => ({
            ...opt,
            values: opt.values.filter((v) => v && v.trim() !== ""),
          }))
          .filter((opt) => opt.name && opt.values.length > 0);

        const cleanedVariants = variants
          .filter(
            (v) =>
              v.option1 &&
              (!cleanedOptions[1] || v.option2) &&
              (!cleanedOptions[2] || v.option3)
          )
          .map((v) => ({ ...v, tracked: true }));

        // Prepare final payload
        const payload = {
          marketIds: formik.values.markets,
          collectionIds: values.collectionIds,
          product: {
            title: values.title,
            body_html: values.body_html,
            vendor: values.vendor,
            product_type: values.product_type,
            tags: values.tags.split(",").map((t) => t.trim()),
            status: values.status,
            metafields: [
              {
                namespace: "availability",
                key: "provinces_oman",
                type: "list.single_line_text_field",
                value: JSON.stringify(values.provinces),
              },
            ],
            options: cleanedOptions,
            variants: cleanedVariants,
            images,
          },
          inventoryByVariant,
        };


        if (id) {
          await axiosInstance.put(`V1/products/update/${id}`, payload);
          showSuccess("Product updated successfully");
        } else {
          const res = await axiosInstance.post(`V1/products`, payload);
          onCreated && onCreated(res.data);
          showSuccess("Product added successfully");
        }

        resetForm();
        setAllImages([]);
        setVariants([]);
        navigate("/products");
      } catch (err) {
        console.error(
          "Failed to create product:",
          err.response?.data || err.message
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ================= FETCH DATA =================
  useEffect(() => {
    dispatch(fetchUsersRequest());
  }, []);

  useEffect(() => {
    if (!id) return;

    axiosInstance.get(`V1/products/${id}`).then((res) => {
      const { product, marketIds, collectionIds, inventoryByVariant } =
        res.data;
      let parsedProvinces = [];

      const provincesMetafield = product.metafields?.find(
        (mf) =>
          mf.namespace === "availability" &&
          mf.key === "provinces_oman"
      );

      if (provincesMetafield?.value) {
        try {
          parsedProvinces = JSON.parse(provincesMetafield.value);
        } catch (err) {
          console.error("Failed to parse provinces metafield", err);
        }
      }

      setProductDetails(product);

      setAllImages(
        product.images?.map((img) => ({
          src: img.src || img.originalSrc,
          isNew: false,
        })) || []
      );

      setVariants(product.variants || []);

      formik.setValues({
        title: product.title || "",
        body_html: product.body_html || "",
        vendor: product.vendor || "",
        product_type: product.product_type || "",
        status: product.status,
        tags: Array.isArray(product.tags)
          ? product.tags.join(", ")
          : product.tags || "",
        price: product.variants?.[0]?.price || "",
        options: product.options || [],
        images: [],
        collectionIds: collectionIds?.map(String) || [],
        provinces: parsedProvinces,
        metafields: { vendor_name: "", vendor_id: "" },
        markets: marketIds || [],
      });

      //  MAP INVENTORY CORRECTLY
      const mappedMarketInventory = {};

      product.variants.forEach((variant, idx) => {
        mappedMarketInventory[idx] = {};

        const skuInventory = inventoryByVariant?.[variant.sku] || [];

        skuInventory.forEach((inv) => {
          mappedMarketInventory[idx][inv.locationId] = inv.quantity;
        });
      });

      setMarketInventory(mappedMarketInventory);
    });
  }, [id, marketsList, locations]);

  useEffect(() => {
    axiosInstance
      .get("V1/collections")
      .then((res) => setCollections(res.data.data || []));
    axiosInstance.get("V1/products/market/All").then((res) => {
      const markets = res.data?.markets || [];
      const formatted = markets
        .filter((m) => m.enabled)
        .map((m) => ({
          label: m.name,
          value: m.id,
          countryCodes:
            m.conditions?.regionsCondition?.regions?.nodes?.map(
              (r) => r.code
            ) || [],
        }));
      setMarketsList(formatted);
    });
    axiosInstance.get("V1/common/location/all").then((res) => {
      const locs = res.data.data
        .filter((l) => l.active)
        .map((l) => ({
          name: l.name,
          countryCode: l.country_code,
          locationId: l.admin_graphql_api_id,
        }));
      setLocations(locs);
    });
  }, []);

  useEffect(() => {
    axiosInstance.get("V1/products/province/all").then((res) => {
      const list = res.data?.data || [];
      setProvinces(
        list.map((p) => ({
          label: p,
          value: p,
        }))
      );
    });
  }, []);

  useEffect(() => {
    if (isVendor) formik.setFieldValue("vendor", vendorName || "");
  }, [isVendor, vendorName]);

  // ================= GENERATE VARIANTS =================
  useEffect(() => {
    const newVariants = generateVariants(
      formik.values.options,
      formik.values.price || 0
    );
    const merged = newVariants.map((v) => {
      const existing = variants.find(
        (ex) =>
          ex.option1 === v.option1 &&
          ex.option2 === v.option2 &&
          ex.option3 === v.option3
      );
      const sku =
        existing?.sku ||
        [v.option1, v.option2, v.option3]
          .filter(Boolean)
          .join("-")
          .toUpperCase();
      return { ...v, price: existing?.price ?? v.price, sku };
    });
    setVariants(merged);
  }, [formik.values.options, formik.values.price]);

  // ================= INVENTORY BY VARIANT =================
  useEffect(() => {
    const inv = {};
    variants.forEach((variant, idx) => {
      inv[variant.sku] = [];
      formik.values.markets.forEach((marketId) => {
        const locationIds = getLocationIdsByMarketId(marketId);
        // console.log("locationIds for market", marketId, locationIds);
        locationIds.forEach((locId) => {
          inv[variant.sku].push({
            locationId: locId,
            quantity: marketInventory[idx]?.[locId] || 0,
          });
        });
      });
    });
    setInventoryByVariant(inv);
  }, [
    variants,
    formik.values.markets,
    marketInventory,
    locations,
    marketsList,
  ]);

  const getLocationIdsByMarketId = (marketId) => {
    const market = marketsList.find((m) => m.value === marketId);
    if (!market) return [];
    // console.log("locations", locations);
    return locations
      .filter((l) => market.countryCodes.includes(l.countryCode))
      .map((l) => l.locationId);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setAllImages((prev) => [
      ...prev,
      ...files.map((file) => ({
        file,
        src: URL.createObjectURL(file),
        isNew: true,
      })),
    ]);
  };

  const handleImageRemove = (idx) =>
    setAllImages((prev) => prev.filter((_, i) => i !== idx));

  const activeLocations = useMemo(() => {
    const set = new Map();

    formik.values.markets.forEach((marketId) => {
      const market = marketsList.find((m) => m.value === marketId);
      if (!market) return;

      locations
        .filter((l) => market.countryCodes.includes(l.countryCode))
        .forEach((l) => {
          set.set(l.locationId, l);
        });
    });

    return Array.from(set.values());
  }, [formik.values.markets, locations, marketsList]);

  const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Draft", value: "draft" },
    { label: "Archived", value: "archived" },
  ];

  // ================= RENDER =================
  return (

    <div className="page-content">
      <style>
        {`
      .product-description-editor .ql-container {
        min-height: 300px;
      }

      .product-description-editor .ql-editor {
        min-height: 300px;
        overflow-y: auto;
      }
    `}
      </style>
      <div className="d-flex justify-content-between align-items-center mx-3">
        <Breadcrumb
          title={id ? "Update product" : "Create product"}
          breadcrumbItems={[
            { title: "Dashboard", link: "/dashboard" },
            { title: id ? "Update Product" : "Create Product", link: "#" },
          ]}
        />
        <Button
          className="text-white bg-primary"
          onClick={() => navigate("/products")}
        >
          ← Back
        </Button>
      </div>

      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit} className="mx-3">
          <Row>
            {/* LEFT COLUMN */}
            <Col md={8}>
              {/* Title + Description + Media */}
              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >
                <Label>Title*</Label>

                <Input
                  name="title"
                  placeholder="Enter the title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                />
                {formik.errors.title && (
                  <FormText color="danger">{formik.errors.title}</FormText>
                )}
              </div>
              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >
                <Label className="mt-3">Description</Label>
                <ReactQuill
                  theme="snow"
                  className="product-description-editor"
                  value={formik.values.body_html}
                  onChange={(value) => formik.setFieldValue("body_html", value)}

                />
              </div>

              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >
                <Label className="mt-3">Media</Label>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <div className="d-flex gap-2 mt-2 flex-wrap">
                  {allImages.map((img, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img
                        src={img.src}
                        alt=""
                        style={{
                          width: 100,
                          height: 100,
                          objectFit: "cover",
                          borderRadius: 6,
                        }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleImageRemove(i)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          padding: "2px 5px",
                          borderRadius: "50%",
                        }}
                      >
                        <IoMdClose size={12} color="white" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >
                <Label>Price*</Label>
                <Input
                  placeholder="Enter the price"
                  type="number"
                  name="price"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                />
                {formik.errors.price && (
                  <FormText color="danger">{formik.errors.price}</FormText>
                )}
              </div>

              {/* Markets */}
              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >
                <Label>Markets*</Label>
                <Select
                  isMulti
                  options={marketsList}
                  value={marketsList.filter((m) =>
                    formik.values.markets.includes(m.value)
                  )}
                  onChange={(selected) => {
                    const values = selected ? selected.map((s) => s.value) : [];
                    formik.setFieldValue("markets", values);
                    formik.setFieldTouched("markets", true, true);
                  }}
                  onBlur={() => formik.setFieldTouched("markets", true)}
                />

                {formik.touched.markets && formik.errors.markets && (
                  <FormText color="danger">{formik.errors.markets}</FormText>
                )}


              </div>

              {/* Options */}
              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >
                <FieldArray name="options">
                  {({ push, remove }) => (
                    <div>
                      {formik.values.options.map((option, idx) => (
                        <div key={idx} className="border p-2 mb-2">
                          <Row>
                            <Col md={10}>
                              <Field
                                name={`options.${idx}.name`}
                                as={Input}
                                placeholder={`Option ${idx + 1} name`}
                              />
                            </Col>

                            <Col md={2} className="text-end">
                              {formik.values.options.length > 1 && (
                                <FaRegTrashAlt
                                  style={{ cursor: "pointer" }}
                                  onClick={() => remove(idx)}
                                />
                              )}
                            </Col>
                          </Row>

                          {option.values.map((v, vIdx) => (
                            <div key={vIdx} className="d-flex gap-2 mt-2">
                              <Field
                                name={`options.${idx}.values.${vIdx}`}
                                as={Input}
                                placeholder="Value"
                              />

                              <FaRegTrashAlt
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  const updated = [...option.values];
                                  updated.splice(vIdx, 1);
                                  formik.setFieldValue(
                                    `options.${idx}.values`,
                                    updated.length ? updated : [""]
                                  );
                                }}
                              />

                              {vIdx === option.values.length - 1 && (
                                <BsPlusSquareDotted
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    formik.setFieldValue(
                                      `options.${idx}.values`,
                                      [...option.values, ""]
                                    )
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      ))}

                      {formik.values.options.length < 3 && (
                        <Button
                          size="sm"
                          onClick={() =>
                            push({
                              name:
                                formik.values.options.length === 1
                                  ? "Size"
                                  : "Material",
                              values: [""],
                            })
                          }
                        >
                          Add option
                        </Button>
                      )}
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* ---------- VARIANTS + MARKET INVENTORY ---------- */}
              {variants.length > 0 && formik.values.markets.length > 0 && (
                <div
                  className="p-4 mb-3"
                  style={{ background: "#fff", borderRadius: 8 }}
                >
                  <div className="fw-bold mb-2">Variants Inventory</div>

                  {/* Header row */}
                  <Row className="fw-bold mb-2">
                    <Col md={3}>Variant</Col>
                    <Col md={3}>Price</Col>

                    {activeLocations.map((loc) => (
                      <Col key={loc.locationId} md={2}>
                        {loc.name}
                      </Col>
                    ))}
                  </Row>

                  {/* Variant rows */}
                  {variants.map((variant, idx) => (
                    <Row key={idx} className="align-items-center mb-2">
                      <Col md={3}>
                        {[variant.option1, variant.option2, variant.option3]
                          .filter(Boolean)
                          .join(" - ")}
                      </Col>

                      <Col md={3}>
                        <Input
                          type="number"
                          placeholder="Enter the price"
                          value={variant.price}
                          min={0}
                          onChange={(e) => {
                            const updated = [...variants];
                            updated[idx].price = Number(e.target.value);
                            setVariants(updated);
                          }}
                        />
                      </Col>

                      {activeLocations.map((loc) => (
                        <Col key={loc.locationId} md={2}>
                          <Input
                            type="number"
                            placeholder="Qty"
                            min={0}
                            value={marketInventory[idx]?.[loc.locationId] || ""}
                            onChange={(e) => {
                              setMarketInventory((prev) => ({
                                ...prev,
                                [idx]: {
                                  ...prev[idx],
                                  [loc.locationId]: Number(e.target.value),
                                },
                              }));
                            }}
                          />
                        </Col>
                      ))}
                    </Row>
                  ))}
                </div>
              )}
            </Col>

            {/* RIGHT COLUMN */}
            <Col md={4}>
              <div
                className="p-4 mb-3"
                style={{ background: "#fff", borderRadius: 8 }}
              >

                <Label>Status</Label>
                <Select
                  className="mb-2"
                  options={statusOptions}
                  value={statusOptions.find(
                    (opt) => opt.value === formik.values.status
                  )}
                  onChange={(selected) =>
                    formik.setFieldValue("status", selected.value)
                  }
                />
                <Label>Vendor</Label>
                {isAdmin ? (
                  <Select
                    options={vendors.map((v) => ({
                      label: v.name,
                      value: v.name,
                    }))}
                    value={
                      vendors
                        .map((v) => ({ label: v.name, value: v.name }))
                        .find((opt) => opt.value === formik.values.vendor) ||
                      null
                    }
                    onChange={(selected) =>
                      formik.setFieldValue("vendor", selected.value)
                    }
                  />
                ) : (
                  <Input name="vendor" value={vendorName} readOnly disabled />
                )}
                <Label className="mt-3">Available Provinces</Label>
                <Select
                  isMulti
                  options={provinces}
                  value={provinces.filter((p) =>
                    formik.values.provinces.includes(p.value)
                  )}
                  onChange={(selected) =>
                    formik.setFieldValue(
                      "provinces",
                      selected ? selected.map((s) => s.value) : []
                    )
                  }
                />

                <Label className="mt-3">Product Type</Label>
                <Input
                  placeholder="Enter product type"
                  name="product_type"
                  value={formik.values.product_type}
                  onChange={formik.handleChange}
                />

                <Label className="mt-3">Collections</Label>
                <Select
                  isMulti
                  options={collections?.map((c) => ({
                    label: c.title,
                    value: String(c.id),
                  }))}
                  value={collections
                    ?.filter((c) =>
                      formik.values.collectionIds.includes(String(c.id))
                    )
                    .map((c) => ({
                      label: c.title,
                      value: String(c.id),
                    }))}
                  onChange={(selected) =>
                    formik.setFieldValue(
                      "collectionIds",
                      selected?.map((s) => String(s.value)) || []
                    )
                  }
                />

                <Label className="mt-3">Tags</Label>
                <Input
                  placeholder="Enter the tags sepreated by coma"
                  name="tags"
                  value={formik.values.tags}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
          </Row>

          <Button
            color="primary"
            type="submit"
            className="mb-3"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <>
                <Spinner size="sm" />
                {id ? "Updating..." : "Creating..."}
              </>
            ) : (
              id ? "Update Product" : "Create Product"
            )}
          </Button>
        </Form>
      </FormikProvider>
    </div>
  );
};

export default ProductForm;
